import React, { useEffect, useState, useCallback } from 'react';
import { Image as ImageIcon, Plus, Trash2, Save, Eye, EyeOff, MoveUp, MoveDown, ExternalLink, LayoutGrid, Sparkles, GalleryHorizontal } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api, formatApiError } from '../../lib/api';
import ImageUploader from '../../components/ImageUploader';

const GALLERY_KEYS = [
  { key: 'past-presidents-photos', label: 'Expresidentes', publicPath: '/es/expresidentes', icon: Sparkles },
  { key: 'gallery', label: 'Galería general', publicPath: '/es/galeria', icon: GalleryHorizontal },
];

const VIEW_MODES = [
  { key: 'grid', label: 'Cuadrícula uniforme', hint: 'Todas las fotos del mismo tamaño (cuadradas).' },
  { key: 'mosaic', label: 'Mosaico / Masonry', hint: 'Conserva las proporciones naturales de cada foto.' },
  { key: 'carousel', label: 'Carrusel horizontal', hint: 'Scroll horizontal con tarjetas grandes.' },
];

// 4-option visibility selector for the Past-Presidents gallery (collapses the
// independent `enabled` + `display_mode` controls into a single intuitive picker).
const VISIBILITY_OPTIONS_PP = [
  { key: 'hidden',           enabled: false, display_mode: 'own_page',  label: 'Oculta al público',                hint: 'No aparece en ningún lado del sitio público.' },
  { key: 'on_expresidentes', enabled: true,  display_mode: 'embedded',  label: 'Solo en /expresidentes',           hint: 'Embebida dentro de la página Ex-Presidentes. Sin link en el menú.' },
  { key: 'own_page',         enabled: true,  display_mode: 'own_page',  label: 'Solo en página propia',            hint: 'Aparece en /fotos-ex-presidentes con su propio link en el menú.' },
  { key: 'both',             enabled: true,  display_mode: 'both',      label: 'En ambas',                         hint: 'Página propia con link en el menú Y embebida dentro de /expresidentes.' },
];

function ppVisibilityKey(doc) {
  if (!doc?.enabled) return 'hidden';
  return VISIBILITY_OPTIONS_PP.find((o) => o.enabled && o.display_mode === (doc.display_mode || 'own_page'))?.key || 'own_page';
}

const EMPTY_ITEM = () => ({
  id: '', image_url: '', caption_es: '', caption_en: '',
  description_es: '', description_en: '', date: '', role: '', sort_order: 0,
});

export default function AdminPhotoGalleriesPage() {
  const [activeKey, setActiveKey] = useState(GALLERY_KEYS[0].key);
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedToast, setSavedToast] = useState('');
  // Tracks unsaved edits — prevents silent data loss when switching tabs.
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async (key) => {
    setLoading(true); setError('');
    try {
      const { data } = await api.get(`/admin/photo-galleries/${key}`);
      setDoc({
        ...data,
        items: (data.items || []).map((it) => ({ ...EMPTY_ITEM(), ...it })),
      });
      setDirty(false);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
      setDoc(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(activeKey); }, [activeKey, load]);

  function patch(partial) { setDoc((d) => ({ ...d, ...partial })); setDirty(true); }
  function updateItem(idx, partial) {
    setDoc((d) => ({ ...d, items: d.items.map((it, i) => (i === idx ? { ...it, ...partial } : it)) }));
    setDirty(true);
  }
  function addItem() {
    setDoc((d) => ({ ...d, items: [...d.items, { ...EMPTY_ITEM(), sort_order: (d.items.length + 1) * 10 }] }));
    setDirty(true);
  }
  function removeItem(idx) {
    setDoc((d) => ({ ...d, items: d.items.filter((_, i) => i !== idx) }));
    setDirty(true);
  }
  function moveItem(idx, dir) {
    setDoc((d) => {
      const items = [...d.items];
      const j = idx + dir;
      if (j < 0 || j >= items.length) return d;
      [items[idx], items[j]] = [items[j], items[idx]];
      return { ...d, items: items.map((it, i) => ({ ...it, sort_order: (i + 1) * 10 })) };
    });
    setDirty(true);
  }

  function trySwitchTab(nextKey) {
    if (nextKey === activeKey) return;
    if (dirty) {
      const ok = window.confirm('Tienes cambios sin guardar en esta galería. Si cambias de pestaña los perderás. ¿Continuar sin guardar?');
      if (!ok) return;
    }
    setActiveKey(nextKey);
  }

  async function save() {
    if (!doc) return;
    setSaving(true); setError('');
    try {
      const payload = {
        enabled: !!doc.enabled,
        title_es: doc.title_es || '',
        title_en: doc.title_en || '',
        view_mode: doc.view_mode || 'grid',
        display_mode: doc.display_mode || 'own_page',
        items: doc.items.map((it, i) => ({
          id: it.id || undefined,
          image_url: it.image_url,
          caption_es: it.caption_es || '',
          caption_en: it.caption_en || '',
          description_es: it.description_es || '',
          description_en: it.description_en || '',
          date: it.date || '',
          role: it.role || '',
          sort_order: typeof it.sort_order === 'number' ? it.sort_order : (i + 1) * 10,
        })),
      };
      const { data } = await api.put(`/admin/photo-galleries/${activeKey}`, payload);
      setDoc({ ...data, items: (data.items || []).map((it) => ({ ...EMPTY_ITEM(), ...it })) });
      setDirty(false);
      setSavedToast('Cambios guardados');
      setTimeout(() => setSavedToast(''), 2500);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
    setSaving(false);
  }

  const activeGallery = GALLERY_KEYS.find((g) => g.key === activeKey);

  return (
    <div data-testid="admin-photo-galleries-page" className="space-y-6 max-w-5xl">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-charcoal-800 flex items-center gap-2">
            <ImageIcon size={22} />Galerías de fotos
          </h1>
          <p className="text-charcoal-500 text-sm mt-1">
            Sube fotografías para la sección de Expresidentes y la galería general del sitio.
          </p>
        </div>
        {activeGallery && (
          <Link
            to={activeGallery.publicPath}
            target="_blank"
            rel="noreferrer"
            data-testid="gallery-view-public-link"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-cmic-700 bg-cmic-50 border border-cmic-200 hover:bg-cmic-100 rounded-lg px-3 py-2"
          >
            <ExternalLink size={13} />Ver página pública
          </Link>
        )}
      </header>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-gray-200" data-testid="gallery-tabs">
        {GALLERY_KEYS.map((g) => {
          const Icon = g.icon;
          const active = activeKey === g.key;
          return (
            <button
              key={g.key}
              onClick={() => trySwitchTab(g.key)}
              data-testid={`gallery-tab-${g.key}`}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                active
                  ? 'border-cmic-500 text-cmic-700'
                  : 'border-transparent text-charcoal-500 hover:text-charcoal-800 hover:border-gray-300'
              }`}
            >
              <Icon size={15} />{g.label}
              {active && dirty && (
                <span data-testid="gallery-dirty-badge" className="ml-1 inline-flex items-center text-[10px] font-semibold uppercase tracking-wider text-amber-700 bg-amber-100 border border-amber-200 rounded-full px-1.5 py-0.5">
                  Sin guardar
                </span>
              )}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-3 border-cmic-200 border-t-cmic-500 rounded-full animate-spin" /></div>
      ) : !doc ? (
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 text-sm text-rose-700">{error || 'No se pudo cargar la galería.'}</div>
      ) : (
        <>
          {/* Settings card */}
          <div className="bg-white border border-gray-200 rounded-2xl p-5 sm:p-6 space-y-5">
            {activeKey === 'past-presidents-photos' ? (
              <div data-testid="gallery-visibility-section">
                <label className="block text-xs font-medium text-charcoal-600 mb-2 flex items-center gap-1.5">
                  <Eye size={13} />Visibilidad de la galería
                </label>
                <div className="grid sm:grid-cols-2 gap-2">
                  {VISIBILITY_OPTIONS_PP.map((opt) => {
                    const active = ppVisibilityKey(doc) === opt.key;
                    return (
                      <button
                        key={opt.key}
                        onClick={() => patch({ enabled: opt.enabled, display_mode: opt.display_mode })}
                        data-testid={`gallery-visibility-${opt.key}`}
                        className={`text-left rounded-xl border px-4 py-3 transition-colors ${
                          active
                            ? (opt.key === 'hidden'
                                ? 'border-rose-400 bg-rose-50 ring-2 ring-rose-400/20'
                                : 'border-cmic-500 bg-cmic-50 ring-2 ring-cmic-500/20')
                            : 'border-gray-200 bg-white hover:border-cmic-300'
                        }`}
                      >
                        <div className={`text-sm font-semibold flex items-center gap-1.5 ${
                          active && opt.key === 'hidden' ? 'text-rose-700' : 'text-charcoal-800'
                        }`}>
                          {opt.key === 'hidden' ? <EyeOff size={13} /> : <Eye size={13} />}
                          {opt.label}
                        </div>
                        <div className="text-[11px] text-charcoal-500 mt-0.5 leading-snug">{opt.hint}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-4">
                <button
                  onClick={() => patch({ enabled: !doc.enabled })}
                  data-testid="gallery-toggle-enabled"
                  className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                    doc.enabled
                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100'
                      : 'bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100'
                  }`}
                >
                  {doc.enabled ? <><Eye size={15} />Visible al público</> : <><EyeOff size={15} />Oculta al público</>}
                </button>
                <p className="text-xs text-charcoal-500 leading-relaxed pt-2">
                  Cuando está oculta, los visitantes no pueden ver esta galería; sólo los administradores con permiso &laquo;Galerías&raquo;.
                </p>
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Título (Español)</label>
                <input
                  value={doc.title_es}
                  onChange={(e) => patch({ title_es: e.target.value })}
                  data-testid="gallery-title-es"
                  className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Title (English)</label>
                <input
                  value={doc.title_en}
                  onChange={(e) => patch({ title_en: e.target.value })}
                  data-testid="gallery-title-en"
                  className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-charcoal-600 mb-2 flex items-center gap-1.5">
                <LayoutGrid size={13} />Diseño de la galería
              </label>
              <div className="grid sm:grid-cols-3 gap-2">
                {VIEW_MODES.map((vm) => (
                  <button
                    key={vm.key}
                    onClick={() => patch({ view_mode: vm.key })}
                    data-testid={`gallery-viewmode-${vm.key}`}
                    className={`text-left rounded-xl border px-4 py-3 transition-colors ${
                      doc.view_mode === vm.key
                        ? 'border-cmic-500 bg-cmic-50 ring-2 ring-cmic-500/20'
                        : 'border-gray-200 bg-white hover:border-cmic-300'
                    }`}
                  >
                    <div className="text-sm font-semibold text-charcoal-800">{vm.label}</div>
                    <div className="text-[11px] text-charcoal-500 mt-0.5 leading-snug">{vm.hint}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Items */}
          <div className="bg-white border border-gray-200 rounded-2xl p-5 sm:p-6 space-y-4">
            <div className="flex items-center justify-between gap-3">
              <h3 className="font-semibold text-charcoal-800">
                Fotos ({doc.items.length})
              </h3>
              <button
                onClick={addItem}
                data-testid="gallery-add-item"
                className="inline-flex items-center gap-2 bg-cmic-600 text-white rounded-lg px-3 py-2 text-sm font-medium hover:bg-cmic-700"
              >
                <Plus size={14} />Añadir foto
              </button>
            </div>

            {doc.items.length === 0 ? (
              <div className="border-2 border-dashed border-gray-200 rounded-xl p-10 text-center text-charcoal-500">
                <ImageIcon size={32} className="mx-auto mb-3 text-charcoal-300" />
                <p className="text-sm">Aún no hay fotos. Haz click en &laquo;Añadir foto&raquo; para empezar.</p>
              </div>
            ) : (
              <ul className="space-y-3">
                {doc.items.map((it, idx) => (
                  <li
                    key={`${idx}-${it.id || 'new'}`}
                    data-testid={`gallery-item-${idx}`}
                    className="border border-gray-200 rounded-xl p-4 bg-gray-50/50"
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex flex-col items-center gap-1 pt-1">
                        <span className="text-xs font-semibold text-charcoal-500 tabular-nums w-6 text-center">{idx + 1}</span>
                        <button
                          onClick={() => moveItem(idx, -1)}
                          disabled={idx === 0}
                          data-testid={`gallery-item-${idx}-up`}
                          className="p-1 text-charcoal-500 hover:text-cmic-600 disabled:opacity-30"
                          title="Subir"
                        >
                          <MoveUp size={14} />
                        </button>
                        <button
                          onClick={() => moveItem(idx, 1)}
                          disabled={idx === doc.items.length - 1}
                          data-testid={`gallery-item-${idx}-down`}
                          className="p-1 text-charcoal-500 hover:text-cmic-600 disabled:opacity-30"
                          title="Bajar"
                        >
                          <MoveDown size={14} />
                        </button>
                      </div>
                      <div className="flex-1 min-w-0 space-y-3">
                        <div className="flex items-start gap-4 flex-wrap">
                          {it.image_url ? (
                            <img src={it.image_url} alt="" className="w-24 h-24 object-cover rounded-lg ring-1 ring-gray-200 flex-shrink-0" />
                          ) : null}
                          <div className="flex-1 min-w-[220px]">
                            <ImageUploader
                              value={it.image_url}
                              onChange={(url) => updateItem(idx, { image_url: url })}
                              testid={`gallery-item-${idx}-photo`}
                              shape="rect"
                              cropAspect={activeKey === 'past-presidents-photos' ? 4 / 5 : 4 / 3}
                              label={it.image_url ? '' : 'Foto (se aplicará recorte)'}
                            />
                          </div>
                        </div>

                        <div className="grid sm:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-charcoal-600 mb-1">Título (ES)</label>
                            <input
                              value={it.caption_es}
                              onChange={(e) => updateItem(idx, { caption_es: e.target.value })}
                              data-testid={`gallery-item-${idx}-caption-es`}
                              placeholder="Ej: Asamblea General 2024"
                              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-charcoal-600 mb-1">Title (EN)</label>
                            <input
                              value={it.caption_en}
                              onChange={(e) => updateItem(idx, { caption_en: e.target.value })}
                              data-testid={`gallery-item-${idx}-caption-en`}
                              placeholder="Ej: General Assembly 2024"
                              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-charcoal-600 mb-1">Fecha (libre)</label>
                            <input
                              value={it.date}
                              onChange={(e) => updateItem(idx, { date: e.target.value })}
                              data-testid={`gallery-item-${idx}-date`}
                              placeholder='Ej: "2024", "Marzo 2024", "2018-2020"'
                              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-charcoal-600 mb-1">Cargo / Rol</label>
                            <input
                              value={it.role}
                              onChange={(e) => updateItem(idx, { role: e.target.value })}
                              data-testid={`gallery-item-${idx}-role`}
                              placeholder='Ej: "Presidenta", "Tesorera"'
                              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500"
                            />
                          </div>
                          <div className="sm:col-span-2">
                            <label className="block text-xs font-medium text-charcoal-600 mb-1">Descripción (ES)</label>
                            <textarea
                              value={it.description_es}
                              onChange={(e) => updateItem(idx, { description_es: e.target.value })}
                              data-testid={`gallery-item-${idx}-desc-es`}
                              rows={2}
                              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500"
                            />
                          </div>
                          <div className="sm:col-span-2">
                            <label className="block text-xs font-medium text-charcoal-600 mb-1">Description (EN)</label>
                            <textarea
                              value={it.description_en}
                              onChange={(e) => updateItem(idx, { description_en: e.target.value })}
                              data-testid={`gallery-item-${idx}-desc-en`}
                              rows={2}
                              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500"
                            />
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => { if (window.confirm('¿Eliminar esta foto?')) removeItem(idx); }}
                        data-testid={`gallery-item-${idx}-delete`}
                        className="p-2 text-rose-500 hover:bg-rose-50 rounded-lg"
                        title="Eliminar"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Save bar */}
          <div className="sticky bottom-4 z-10">
            <div className="flex items-center justify-between gap-3 bg-white rounded-2xl shadow-lg border border-gray-200 px-5 py-3">
              <div className="text-xs text-charcoal-500">
                {error ? (
                  <span className="text-rose-600">{error}</span>
                ) : savedToast ? (
                  <span className="text-emerald-600 font-medium">{savedToast}</span>
                ) : dirty ? (
                  <span className="text-amber-700 font-medium">Hay cambios sin guardar.</span>
                ) : (
                  <>Recuerda guardar después de editar.</>
                )}
              </div>
              <button
                onClick={save}
                disabled={saving || !dirty}
                data-testid="gallery-save-btn"
                className={`inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold ${
                  dirty
                    ? 'bg-cmic-600 text-white hover:bg-cmic-700'
                    : 'bg-charcoal-100 text-charcoal-400'
                } disabled:opacity-50`}
              >
                <Save size={15} />{saving ? 'Guardando…' : 'Guardar cambios'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
